/**
 * Lightweight 3D viewers for the predimensionamiento module. They are simpler
 * than the design-module viewers (no per-bar configuration) — just enough to
 * communicate the geometry visually.
 */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid, Bounds } from '@react-three/drei';
import * as THREE from 'three';

const COLOR_CONCRETE = '#94A3B8';
const COLOR_EDGE = '#475569';
const COLOR_BAR = '#F59E0B';
const COLOR_STIRRUP = '#0891B2';
const COLOR_SOIL = '#7C2D12';
const COLOR_BRICK = '#A16207';

/** Standard canvas wrapper — common lights, grid and orbit controls. */
function PredimCanvas({ children, gridY = -1 }: { children: React.ReactNode; gridY?: number }) {
  return (
    <Canvas
      camera={{ position: [6, 5, 7], fov: 38, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
      gl={{ preserveDrawingBuffer: true }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 6, 5]} intensity={0.95} />
      <directionalLight position={[-4, -2, -3]} intensity={0.28} />
      <Grid
        args={[20, 20]}
        cellSize={0.25}
        cellThickness={0.4}
        cellColor="#475569"
        sectionSize={1}
        sectionThickness={1}
        sectionColor="#334155"
        fadeDistance={14}
        fadeStrength={1.2}
        position={[0, gridY, 0]}
        infiniteGrid
      />
      <Bounds fit clip observe margin={1.45}>
        {children}
      </Bounds>
      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

/** Reusable cylinder used for bars. */
function Bar({ radius, length, position, rotation, color = COLOR_BAR }: {
  radius: number;
  length: number;
  position: [number, number, number];
  rotation: [number, number, number];
  color?: string;
}) {
  const geom = useMemo(
    () => new THREE.CylinderGeometry(radius, radius, length, 14),
    [radius, length],
  );
  return (
    <mesh position={position} rotation={rotation} geometry={geom}>
      <meshStandardMaterial color={color} metalness={0.45} roughness={0.4} />
    </mesh>
  );
}

/** Concrete box with crisp wireframe edges. */
function ConcreteBox({ size, position, opacity = 0.42 }: {
  size: [number, number, number];
  position: [number, number, number];
  opacity?: number;
}) {
  return (
    <group position={position}>
      <mesh>
        <boxGeometry args={size} />
        <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={opacity} roughness={0.85} />
      </mesh>
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(...size)]} />
        <lineBasicMaterial color={COLOR_EDGE} />
      </lineSegments>
    </group>
  );
}

/* ====================================================================== */
/*  COLUMN 3D                                                               */
/* ====================================================================== */

export function Column3D({ b, h, hCol = 280 }: { b: number; h: number; hCol?: number }) {
  // Scale so the longest dimension fits ~5 scene units
  const scale = 5 / Math.max(hCol, b, h, 1);
  const bs = b * scale;
  const hs = h * scale;
  const Hs = hCol * scale;
  const cover = 4;
  const off = (cover + 2) * scale;
  // 4 corner bars
  const positions: [number, number][] = [
    [-bs / 2 + off, -hs / 2 + off],
    [+bs / 2 - off, -hs / 2 + off],
    [-bs / 2 + off, +hs / 2 - off],
    [+bs / 2 - off, +hs / 2 - off],
  ];
  // Stirrup loop geometry (closed rectangle in the x-z plane)
  const stirrup = useMemo(() => {
    const half_b = bs / 2 - cover * scale;
    const half_h = hs / 2 - cover * scale;
    const pts = [
      new THREE.Vector3(-half_b, 0, -half_h),
      new THREE.Vector3(+half_b, 0, -half_h),
      new THREE.Vector3(+half_b, 0, +half_h),
      new THREE.Vector3(-half_b, 0, +half_h),
      new THREE.Vector3(-half_b, 0, -half_h),
    ];
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, [bs, hs, scale]);

  // Stirrup spacing — every ~15 cm
  const nStirrups = Math.max(3, Math.floor(hCol / 15));
  const stirrupYs = Array.from({ length: nStirrups }, (_, i) => -Hs / 2 + (i + 0.5) * (Hs / nStirrups));

  return (
    <PredimCanvas gridY={-Hs / 2 - 0.01}>
      <ConcreteBox size={[bs, Hs, hs]} position={[0, 0, 0]} />
      {/* 4 corner bars running vertically */}
      {positions.map((p, i) => (
        <Bar
          key={i}
          radius={Math.max((2.5 / 2) * scale, 0.018)}
          length={Hs}
          position={[p[0], 0, p[1]]}
          rotation={[0, 0, 0]}
          color={COLOR_BAR}
        />
      ))}
      {/* Stirrups */}
      {stirrupYs.map((y, i) => (
        <line key={`stir-${i}`} position={[0, y, 0]}>
          <primitive attach="geometry" object={stirrup} />
          <lineBasicMaterial color={COLOR_STIRRUP} />
        </line>
      ))}
    </PredimCanvas>
  );
}

/* ====================================================================== */
/*  BEAM 3D                                                                 */
/* ====================================================================== */

export function Beam3D({ b, h, L }: { b: number; h: number; L: number }) {
  const scale = 5 / Math.max(L, h * 5, b * 5, 1);
  const Ls = L * scale;
  const hs = h * scale;
  const bs = b * scale;
  const cover = 4;
  const off = (cover + 2) * scale;

  // 2 top + 2 bottom bars along the span (x axis)
  const bars = [
    { y: +hs / 2 - off, z: -bs / 2 + off, color: '#F59E0B' },
    { y: +hs / 2 - off, z: +bs / 2 - off, color: '#F59E0B' },
    { y: -hs / 2 + off, z: -bs / 2 + off, color: '#F59E0B' },
    { y: -hs / 2 + off, z: +bs / 2 - off, color: '#F59E0B' },
  ];

  // Stirrups
  const stirrup = useMemo(() => {
    const half_h = hs / 2 - cover * scale;
    const half_b = bs / 2 - cover * scale;
    const pts = [
      new THREE.Vector3(0, +half_h, -half_b),
      new THREE.Vector3(0, +half_h, +half_b),
      new THREE.Vector3(0, -half_h, +half_b),
      new THREE.Vector3(0, -half_h, -half_b),
      new THREE.Vector3(0, +half_h, -half_b),
    ];
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, [hs, bs, scale]);
  const nStir = Math.max(3, Math.floor(L / 15));
  const stirrupXs = Array.from({ length: nStir }, (_, i) => -Ls / 2 + (i + 0.5) * (Ls / nStir));

  return (
    <PredimCanvas gridY={-hs / 2 - 0.01}>
      <ConcreteBox size={[Ls, hs, bs]} position={[0, 0, 0]} />
      {bars.map((bar, i) => (
        <Bar
          key={i}
          radius={Math.max((2 / 2) * scale, 0.020)}
          length={Ls}
          position={[0, bar.y, bar.z]}
          rotation={[0, 0, Math.PI / 2]}
          color={bar.color}
        />
      ))}
      {stirrupXs.map((x, i) => (
        <line key={`stir-${i}`} position={[x, 0, 0]}>
          <primitive attach="geometry" object={stirrup} />
          <lineBasicMaterial color={COLOR_STIRRUP} />
        </line>
      ))}
    </PredimCanvas>
  );
}

/* ====================================================================== */
/*  SLAB 3D                                                                 */
/* ====================================================================== */

export function Slab3D({
  h, L, Lb, isRibbed, isTwoWay,
}: { h: number; L: number; Lb?: number; isRibbed: boolean; isTwoWay: boolean }) {
  const La = L;
  const Lbb = isTwoWay && Lb ? Lb : L * 0.7;
  const scale = 5 / Math.max(Lbb, La, 1);
  const Lxs = Lbb * scale;          // x-direction (longer)
  const Lzs = La * scale;
  const hs = h * scale;

  // For ribbed: 5 cm flange + ribs every 40 cm
  const flangeH = isRibbed ? 5 * scale : hs;
  const ribH = isRibbed ? (h - 5) * scale : 0;
  // Show 3 ribs
  const ribW = 10 * scale;
  const ribSpacing_cm = 40;
  const nRibs = Math.max(2, Math.floor(La / ribSpacing_cm));
  const ribStep = Lzs / (nRibs + 1);
  const ribZs = Array.from({ length: nRibs }, (_, i) => -Lzs / 2 + (i + 1) * ribStep);

  return (
    <PredimCanvas gridY={-hs / 2 - 0.01}>
      {/* Top flange (or full slab for solid) */}
      <ConcreteBox
        size={[Lxs, flangeH, Lzs]}
        position={[0, +hs / 2 - flangeH / 2, 0]}
      />
      {/* Ribs (only for ribbed slabs) */}
      {isRibbed && ribZs.map((z, i) => (
        <ConcreteBox
          key={`rib-${i}`}
          size={[Lxs, ribH, ribW]}
          position={[0, +hs / 2 - flangeH - ribH / 2, z]}
        />
      ))}
      {/* Bars in the L direction (along x) */}
      {[-0.3, -0.1, 0.1, 0.3].map((f, i) => (
        <Bar
          key={`bx-${i}`}
          radius={0.015}
          length={Lxs * 0.96}
          position={[0, -hs / 2 + 0.018, f * Lzs]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR}
        />
      ))}
      {/* For 2D, also bars in the Lb direction */}
      {isTwoWay && [-0.3, -0.1, 0.1, 0.3].map((f, i) => (
        <Bar
          key={`bz-${i}`}
          radius={0.014}
          length={Lzs * 0.96}
          position={[f * Lxs, -hs / 2 + 0.04, 0]}
          rotation={[Math.PI / 2, 0, 0]}
          color="#8B5CF6"
        />
      ))}
    </PredimCanvas>
  );
}

/* ====================================================================== */
/*  WALL 3D                                                                 */
/* ====================================================================== */

export function Wall3D({ hw, lw, tw }: { hw: number; lw: number; tw: number }) {
  const scale = 5 / Math.max(lw, hw, 1);
  const lws = lw * scale;
  const hws = hw * scale;
  const tws = tw * scale;

  // Vertical bars in 2 layers across tw, distributed in 5 columns along lw
  const cover = 4;
  const yBarTop = +tws / 2 - (cover + 1.5) * scale;
  const yBarBot = -tws / 2 + (cover + 1.5) * scale;
  const nx = 5;
  const xs = Array.from({ length: nx }, (_, i) =>
    -lws / 2 + (i + 0.5) * (lws / nx),
  );

  return (
    <PredimCanvas gridY={-hws / 2 - 0.01}>
      <ConcreteBox size={[lws, hws, tws]} position={[0, 0, 0]} />
      {/* Vertical bars — 2 layers */}
      {xs.map((x, i) => (
        <group key={i}>
          <Bar radius={0.014} length={hws} position={[x, 0, yBarTop]} rotation={[0, 0, 0]} color={COLOR_BAR} />
          <Bar radius={0.014} length={hws} position={[x, 0, yBarBot]} rotation={[0, 0, 0]} color={COLOR_BAR} />
        </group>
      ))}
      {/* Horizontal mesh — every 30 cm along height, at both faces */}
      {Array.from({ length: Math.max(3, Math.floor(hw / 30)) }, (_, i) => {
        const y = -hws / 2 + (i + 0.5) * (hws / Math.max(3, Math.floor(hw / 30)));
        return (
          <group key={`h-${i}`}>
            <Bar radius={0.012} length={lws * 0.97} position={[0, y, yBarTop]} rotation={[0, 0, Math.PI / 2]} color="#22C55E" />
            <Bar radius={0.012} length={lws * 0.97} position={[0, y, yBarBot]} rotation={[0, 0, Math.PI / 2]} color="#22C55E" />
          </group>
        );
      })}
    </PredimCanvas>
  );
}

/* ====================================================================== */
/*  FOOTING 3D                                                              */
/* ====================================================================== */

export function Footing3D({
  variant, Lx, Ly, Hf, bc = 30, hc = 30, df = 120,
  L_cols, Lx2, Ly2,
}: {
  variant: 'isolated' | 'strip' | 'combined' | 'connected';
  Lx: number; Ly: number; Hf: number;
  bc?: number; hc?: number; df?: number;
  L_cols?: number; Lx2?: number; Ly2?: number;
}) {
  // Scale based on the bounding span
  const span = variant === 'connected' || variant === 'combined'
    ? Math.max(Lx + (L_cols ?? 0) + (Lx2 ?? Lx) / 2, Lx, Ly)
    : Math.max(Lx, Ly, df);
  const scale = 5 / Math.max(span, 1);
  const Lxs = Lx * scale;
  const Lys = Ly * scale;
  const Hfs = Hf * scale;
  const dfs = df * scale;
  const bcs = bc * scale;
  const hcs = hc * scale;

  // y origin: bottom of soil region
  const yOf = (h_cm: number) => -dfs / 2 + (h_cm - df) * scale;

  if (variant === 'isolated') {
    return (
      <PredimCanvas gridY={yOf(df) - 0.01}>
        {/* Soil */}
        <mesh position={[0, yOf(df / 2), 0]}>
          <boxGeometry args={[Lxs * 1.5, dfs, Lys * 1.5]} />
          <meshStandardMaterial color={COLOR_SOIL} transparent opacity={0.10} />
        </mesh>
        {/* Footing slab */}
        <ConcreteBox size={[Lxs, Hfs, Lys]} position={[0, yOf(Hf / 2), 0]} />
        {/* Column stub */}
        <ConcreteBox
          size={[bcs, (df - Hf + 60) * scale, hcs]}
          position={[0, yOf(Hf + (df - Hf + 60) / 2), 0]}
          opacity={0.55}
        />
        {/* Bottom mesh — 4 bars each direction */}
        {[-0.35, -0.15, 0.15, 0.35].map((f, i) => (
          <Bar key={`bx-${i}`}
            radius={0.014}
            length={Lxs * 0.96}
            position={[0, yOf(4), f * Lys]}
            rotation={[0, 0, Math.PI / 2]}
            color={COLOR_BAR} />
        ))}
        {[-0.35, -0.15, 0.15, 0.35].map((f, i) => (
          <Bar key={`bz-${i}`}
            radius={0.014}
            length={Lys * 0.96}
            position={[f * Lxs, yOf(6), 0]}
            rotation={[Math.PI / 2, 0, 0]}
            color="#8B5CF6" />
        ))}
      </PredimCanvas>
    );
  }

  if (variant === 'strip') {
    const L_show = Math.max(300, 4 * (bc || 15));   // bc carries tw for strip
    const Lshow_s = L_show * scale;
    return (
      <PredimCanvas gridY={yOf(df) - 0.01}>
        <mesh position={[0, yOf(df / 2), 0]}>
          <boxGeometry args={[Lshow_s * 1.5, dfs, Lys * 1.5]} />
          <meshStandardMaterial color={COLOR_SOIL} transparent opacity={0.10} />
        </mesh>
        <ConcreteBox size={[Lshow_s, Hfs, Lys]} position={[0, yOf(Hf / 2), 0]} />
        {/* Brick wall above */}
        <mesh position={[0, yOf(Hf + 80) / 2 + yOf(Hf) / 2, 0]}>
          <boxGeometry args={[Lshow_s, (80 * scale), bcs]} />
          <meshStandardMaterial color={COLOR_BRICK} transparent opacity={0.7} roughness={1.0} />
        </mesh>
      </PredimCanvas>
    );
  }

  if (variant === 'combined') {
    const Lcols_s = (L_cols ?? 400) * scale;
    return (
      <PredimCanvas gridY={yOf(df) - 0.01}>
        <mesh position={[0, yOf(df / 2), 0]}>
          <boxGeometry args={[Lxs * 1.4, dfs, Lys * 1.5]} />
          <meshStandardMaterial color={COLOR_SOIL} transparent opacity={0.10} />
        </mesh>
        <ConcreteBox size={[Lxs, Hfs, Lys]} position={[0, yOf(Hf / 2), 0]} />
        {/* 2 columns */}
        {[-Lcols_s / 2, +Lcols_s / 2].map((cx, i) => (
          <ConcreteBox
            key={i}
            size={[bcs, (df - Hf + 60) * scale, hcs]}
            position={[cx, yOf(Hf + (df - Hf + 60) / 2), 0]}
            opacity={0.55}
          />
        ))}
      </PredimCanvas>
    );
  }

  // connected
  const Lcols_s = (L_cols ?? 400) * scale;
  const Lx2s = (Lx2 ?? Lx) * scale;
  const Ly2s = (Ly2 ?? Ly) * scale;
  const xMid = (Lxs + Lcols_s + Lx2s / 2) / 2 - Lxs / 2;
  return (
    <PredimCanvas gridY={yOf(df) - 0.01}>
      {/* Soil block */}
      <mesh position={[xMid, yOf(df / 2), 0]}>
        <boxGeometry args={[Lxs + Lcols_s + Lx2s + 0.5, dfs, Math.max(Lys, Ly2s) * 1.5]} />
        <meshStandardMaterial color={COLOR_SOIL} transparent opacity={0.08} />
      </mesh>
      {/* Property line — red plane at x = -Lxs/2 (left edge of footing 1) */}
      <mesh position={[-Lxs / 2, yOf(df / 2 + 30), 0]}>
        <boxGeometry args={[0.015, dfs + 60 * scale, Math.max(Lys, Ly2s) * 1.5]} />
        <meshBasicMaterial color="#DC2626" transparent opacity={0.45} />
      </mesh>
      {/* Footing 1 (medianera) */}
      <ConcreteBox size={[Lxs, Hfs, Lys]} position={[0, yOf(Hf / 2), 0]} />
      {/* Footing 2 (interior) */}
      <ConcreteBox size={[Lx2s, Hfs, Ly2s]}
        position={[Lxs / 2 + Lcols_s, yOf(Hf / 2), 0]} />
      {/* Columns */}
      <ConcreteBox size={[bcs, (df - Hf + 60) * scale, hcs]}
        position={[-Lxs / 2 + bcs / 2 + 0.05, yOf(Hf + (df - Hf + 60) / 2), 0]} opacity={0.55} />
      <ConcreteBox size={[bcs, (df - Hf + 60) * scale, hcs]}
        position={[Lxs / 2 + Lcols_s, yOf(Hf + (df - Hf + 60) / 2), 0]} opacity={0.55} />
      {/* Connection beam at grade */}
      <ConcreteBox size={[Lcols_s - bcs, 0.18, bcs * 0.7]}
        position={[Lxs / 2 + Lcols_s / 2, yOf(df) - 0.1, 0]} opacity={0.5} />
    </PredimCanvas>
  );
}
